'use client';
import {createContext,useCallback,useContext,useEffect,useLayoutEffect,useRef,useState,useTransition,type ReactNode} from 'react';
import Link from 'next/link';
import {useRouter} from 'next/navigation';
import {formatCny,localizedPath} from '@zzy/contracts';
import {useCreationText} from './creation-client';
import s from './creation-navigation.module.css';
import w from './creation-workspace.module.css';

const items=[
 {id:'image',label:'图片生成',href:'/create/image'},
 {id:'video',label:'视频创作',href:'/create/video'},
 {id:'remix',label:'视频复刻',href:'/create/remix'},
 {id:'works',label:'我的作品',href:'/works'},
] as const;
export type CreationSection=typeof items[number]['id'];
type NavigationProps={current:CreationSection;balance?:string;showBalance?:boolean;onBeforeNavigate?:()=>boolean};
const NavigationContext=createContext<((props:NavigationProps)=>()=>void)|undefined>(undefined);

export const creationSectionForPath=(pathname:string)=>items.find(item=>item.href===pathname)?.id;

export function CreationWorkspaceShell({current,children}:{current:CreationSection;children:ReactNode}){
 const [details,setDetails]=useState<NavigationProps>(),active=useRef<NavigationProps|undefined>(undefined);
 const register=useCallback((props:NavigationProps)=>{
  active.current=props;setDetails(props);
  return()=>{if(active.current===props)active.current=undefined;setDetails(value=>value===props?undefined:value)};
 },[]);
 const pageDetails=details?.current===current?details:undefined;
 return <NavigationContext.Provider value={register}><div className={w.workspace}>
  <CreationNavigationBar current={current} balance={pageDetails?.balance} showBalance={pageDetails!==undefined&&(pageDetails.showBalance??true)} onBeforeNavigate={()=>active.current?.current!==current||active.current.onBeforeNavigate?.()!==false}/>
  <div key={current} className={w.content} data-creation-content>{children}</div>
 </div></NavigationContext.Provider>;
}

export function CreationNavigation({current,balance,showBalance=true,onBeforeNavigate}:NavigationProps){
 const register=useContext(NavigationContext),beforeNavigate=useRef(onBeforeNavigate);
 useLayoutEffect(()=>{beforeNavigate.current=onBeforeNavigate},[onBeforeNavigate]);
 useEffect(()=>register?.({current,balance,showBalance,onBeforeNavigate:()=>beforeNavigate.current?.()??true}),[register,current,balance,showBalance]);
 return register?null:<CreationNavigationBar current={current} balance={balance} showBalance={showBalance} onBeforeNavigate={onBeforeNavigate}/>;
}

function CreationNavigationBar({current,balance,showBalance=true,onBeforeNavigate}:NavigationProps){
 const {t,locale}=useCreationText(),router=useRouter(),[pending,startTransition]=useTransition(),[destination,setDestination]=useState(current);
 const track=useRef<HTMLDivElement>(null),active=pending?destination:current;
 useLayoutEffect(()=>{
  const element=track.current,link=element?.querySelector<HTMLAnchorElement>('[data-active=true]');
  if(!element||!link)return;
  const measure=()=>{element.style.setProperty('--tab-left',`${link.offsetLeft}px`);element.style.setProperty('--tab-width',`${link.offsetWidth}px`);element.dataset.ready='true'};
  measure();const observer=new ResizeObserver(measure);observer.observe(element);observer.observe(link);
  return()=>observer.disconnect();
 },[active,locale]);
 return <div className={s.bar} data-creation-navigation data-pending={pending}>
  <nav className={s.navigation} aria-label={t('创作工作台')} aria-busy={pending}>
   <div ref={track} className={s.track}>
    <span className={s.indicator} aria-hidden="true"/>
    {items.map(item=><Link key={item.id} className={item.id==='works'?s.works:undefined} href={localizedPath(item.href,locale)} prefetch={false} scroll={false} aria-current={current===item.id?'page':undefined} data-active={active===item.id} onNavigate={event=>{
     event.preventDefault();
     if(current===item.id||pending&&destination===item.id||onBeforeNavigate&&!onBeforeNavigate())return;
     setDestination(item.id);startTransition(()=>router.push(localizedPath(item.href,locale),{scroll:false}));
    }}>{t(item.label)}</Link>)}
   </div>
  </nav>
  {pending&&<span className="sr-only" role="status">{t('正在切换页面…')}</span>}
  <span className={s.balance} data-empty={!showBalance} aria-hidden={!showBalance}>{showBalance?<>{t('可用余额')} {balance!==undefined?formatCny(balance):t('余额待核对')}</>:'\u00a0'}</span>
 </div>;
}
